import React, { useEffect } from 'react';
import { 
  Box, 
  Grid, 
  VStack, 
  Heading, 
  Text,
  SimpleGrid,
  HStack,
  Icon,
  Flex,
  useColorModeValue,
  Circle,
} from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { Clock, FileText, Zap, MessageCircle, CheckCircle } from 'lucide-react';
import { MethodistSlide } from '../../components/base/MethodistSlide';
import UnifiedMobilePlatform from '../ui-samples/UnifiedMobilePlatform';
import { useAudio } from '../../context/AudioContext';

// Methodist color palette
const methodistBlue = '#0066CC';
const methodistNavy = '#002D72';
const methodistGreen = '#38A169';
const methodistPurple = '#805AD5';

/**
 * Presenter's Guide for WorkplaceDeepDive Slide:
 * 
 * 1. Introduction (30 seconds):
 *    - Introduce the Workplace Quotient (WQ) Domain as the third pillar
 *    - Emphasize how this domain optimizes clinical workflows
 *    - Highlight the impact on practice efficiency
 * 
 * 2. Key Features Section (45 seconds):
 *    - Point out the three key features:
 *      a. Time Optimization: Streamlined clinical workflows
 *      b. Knowledge Library: Best practices repository
 *      c. Practice Insights: Efficiency analytics
 * 
 * 3. Performance Metrics (30 seconds):
 *    - Explain the key efficiency metrics
 *    - Emphasize the tangible time savings
 * 
 * 4. Implementation Approach (30 seconds):
 *    - Walk through the optimization process
 *    - Stress the data-driven approach
 * 
 * 5. Mobile Experience Demo (1 minute):
 *    - Showcase the mobile interface
 *    - Demonstrate workflow optimization features
 * 
 * 6. Conclusion (15 seconds):
 *    - Summarize the WQ Domain's impact
 *    - Complete the overview of all three domains
 */

const narrationScript = `Let's explore the Workplace Quotient (WQ) domain of the SentientIQ platform, which optimizes clinical workflows and processes for enhanced efficiency.

Our key features focus on three core areas:
1. Time Optimization - streamlining clinical workflows for maximum efficiency, saving 88% of time
2. Knowledge Library - curated best practices and workflow templates, with 72% adoption rate
3. Practice Insights - crowdsourced efficiency tips and process improvements, showing 84% enhancement

Our implementation approach follows a proven three-step process:
1. Workflow Analysis - identifying optimization opportunities in clinical processes
2. Smart Solutions - deploying AI-powered tools and shared knowledge resources
3. Process Monitoring - tracking efficiency gains and community contributions

For clinicians, the benefits are substantial:
- Reduced time spent on documentation
- Access to proven workflow templates
- Community-validated best practices
- Data-driven process improvements
- Enhanced clinical decision support

Our key metrics demonstrate strong performance:
- Efficiency score at 88/90
- Documentation rating at 72/80
- Optimization level at 84/85

The mobile interface provides intuitive access to all these features, optimized for busy healthcare professionals.`;

export const WorkplaceDeepDive: React.FC = () => {
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const textColor = useColorModeValue('gray.600', 'gray.400');

  // Use the audio context
  const { playAudioForSlide } = useAudio();

  // Play audio when component mounts
  useEffect(() => {
    // Play audio with a slight delay to ensure DOM is ready
    const audioTimer = setTimeout(() => {
      console.log('Starting audio playback for workplace slide...');
      playAudioForSlide('workplace-deepdive');
    }, 800);
    
    // Cleanup function
    return () => {
      clearTimeout(audioTimer);
      // Audio cleanup is handled by AudioContext
    };
  }, [playAudioForSlide]);

  const features = [
    {
      icon: Clock,
      title: "Time Optimization",
      description: "Streamline clinical workflows for maximum efficiency",
      color: methodistBlue,
      impactMetric: "88/90",
      scalabilityInfo: "Efficiency Score"
    },
    {
      icon: FileText,
      title: "Knowledge Library",
      description: "Curated best practices and workflow templates",
      color: methodistGreen,
      impactMetric: "72/80",
      scalabilityInfo: "Documentation Rating"
    },
    {
      icon: Zap,
      title: "Practice Insights",
      description: "Crowdsourced efficiency tips and process improvements",
      color: methodistPurple,
      impactMetric: "84/85",
      scalabilityInfo: "Optimization Level"
    }
  ];

  const implementationSteps = [
    {
      title: "Workflow Analysis",
      description: "Identify optimization opportunities in clinical processes",
      color: methodistBlue,
      duration: "2-4 weeks"
    },
    {
      title: "Smart Solutions",
      description: "Deploy AI-powered tools and shared knowledge resources",
      color: methodistGreen,
      duration: "4-6 weeks"
    },
    {
      title: "Process Monitoring",
      description: "Track efficiency gains and community contributions",
      color: methodistPurple,
      duration: "Ongoing"
    }
  ];

  const benefits = [
    {
      title: "Time Savings",
      description: "88% reduction in documentation time",
      icon: Clock,
      color: methodistBlue
    },
    {
      title: "Best Practices",
      description: "72% adoption of workflow templates",
      icon: CheckCircle,
      color: methodistGreen
    },
    {
      title: "Community Insights",
      description: "84% efficiency improvement",
      icon: MessageCircle,
      color: methodistPurple
    }
  ];

  return (
    <MethodistSlide showLogo={true} captionsText={narrationScript}>
      <Grid templateColumns="2fr 1fr" gap={8} h="100%" py={6}>
        <VStack align="stretch" spacing={8}>
          {/* Introduction */}
          <Box>
            <Heading size="md" color={methodistBlue} mb={3}>
              Workplace Quotient (WQ) Domain
            </Heading>
            <Text fontSize="md" color={textColor}>
              The SentientIQ Workplace Quotient (WQ) platform optimizes clinical workflows and processes for enhanced efficiency.
              Our mobile interface streamlines documentation and provides data-driven insights for continuous improvement.
            </Text>
          </Box>

          {/* Key Features */}
          <Box>
            <Heading size="sm" mb={4}>Key Features</Heading>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              {features.map((feature, index) => (
                <HStack
                  key={index}
                  spacing={4}
                  p={4}
                  bg={bgColor}
                  borderRadius="md"
                  borderWidth="1px"
                  borderColor={borderColor}
                  shadow="sm"
                  as={motion.div}
                  whileHover={{ scale: 1.02 }}
                  transition={{ ease: "easeInOut" }}
                >
                  <Flex
                    align="center"
                    justify="center"
                    bg={`${feature.color}15`}
                    color={feature.color}
                    borderRadius="md"
                    p={2}
                    boxSize="40px"
                  >
                    <Icon as={feature.icon} boxSize={5} />
                  </Flex>
                  <VStack align="start" spacing={1} flex={1}>
                    <Text fontWeight="medium" fontSize="sm">
                      {feature.title}
                    </Text>
                    <Text fontSize="xs" color={textColor}>
                      {feature.description}
                    </Text>
                    <HStack fontSize="xs" spacing={3} mt={1}>
                      <Text color={feature.color} fontWeight="medium">
                        {feature.impactMetric}
                      </Text>
                      <Text color="gray.500">
                        {feature.scalabilityInfo}
                      </Text>
                    </HStack>
                  </VStack>
                </HStack>
              ))}
            </SimpleGrid>
          </Box>

          {/* Implementation Steps and Benefits Grid */}
          <SimpleGrid columns={2} spacing={8}>
            {/* Left Column: Implementation Steps */}
            <Box>
              <Heading size="sm" mb={4}>Implementation Approach</Heading>
              <VStack spacing={4}>
                {implementationSteps.map((step, index) => (
                  <Box
                    key={index}
                    p={4}
                    bg={bgColor}
                    borderRadius="md"
                    borderWidth="1px"
                    borderColor={borderColor}
                    shadow="sm"
                    w="full"
                  >
                    <HStack spacing={4} align="start">
                      <Circle size="24px" bg={`${step.color}15`}>
                        <Text fontSize="sm" fontWeight="bold" color={step.color}>
                          {index + 1}
                        </Text>
                      </Circle>
                      <VStack align="start" spacing={1}>
                        <Text fontWeight="medium" fontSize="sm">
                          {step.title}
                        </Text>
                        <Text fontSize="xs" color={textColor}>
                          {step.description}
                        </Text>
                        <Text fontSize="xs" color={step.color} fontWeight="medium">
                          {step.duration}
                        </Text>
                      </VStack>
                    </HStack>
                  </Box>
                ))}
              </VStack>
            </Box>

            {/* Right Column: Benefits */}
            <Box>
              <Heading size="sm" mb={4}>Clinician Benefits</Heading>
              <VStack spacing={4}>
                {benefits.map((benefit, index) => (
                  <Box
                    key={index}
                    p={4}
                    bg={bgColor}
                    borderRadius="md"
                    borderWidth="1px"
                    borderColor={borderColor}
                    shadow="sm"
                    w="full"
                  >
                    <HStack spacing={4}>
                      <Circle size="32px" bg={`${benefit.color}15`}>
                        <Icon as={benefit.icon} boxSize="18px" color={benefit.color} />
                      </Circle>
                      <VStack align="start" spacing={1}>
                        <Text fontWeight="medium" fontSize="sm">
                          {benefit.title}
                        </Text>
                        <Text fontSize="xs" color={textColor}>
                          {benefit.description}
                        </Text>
                      </VStack>
                    </HStack>
                  </Box>
                ))}
              </VStack>
            </Box>
          </SimpleGrid>
        </VStack>

        {/* Right side: Mobile Platform Demo */}
        <Box>
          <Heading size="sm" mb={2}>Mobile Platform Demo</Heading>
          <Text fontSize="sm" color={textColor} mb={4}>
            Experience the SentientIQ mobile interface in action. Our intuitive design streamlines clinical workflows, 
            provides quick access to best practices, and delivers real-time efficiency insights—all optimized for busy healthcare professionals.
          </Text>
          <UnifiedMobilePlatform 
            activeDomain="WQ"
            defaultTab="efficiency"
          />
        </Box>
      </Grid>
    </MethodistSlide>
  );
};

export default WorkplaceDeepDive;
